import React from "react";

import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";

import type { FeedbackCategory as FBCategory } from "~/__generated__/FeedbackListQuery.graphql";

export type FeedbackCategory = FBCategory;

export const FEEDBACK_CATEGORY_VALUES: { [key: string]: FeedbackCategory } = {
	INBOX: "INBOX",
	NEED_ACTION: "NEED_ACTION",
	REQUEST_SENT: "REQUEST_SENT",
	FEEDBACK_SENT: "FEEDBACK_SENT",
	DRAFT: "DRAFT",
	ARCHIVED: "ARCHIVED",
} as const; // same as FeedbackCategory

export const FEEDBACK_CATEGORY_TEXT_MAP: { [key: string]: string } = {
	/* values in FeedbackCategory */
	INBOX: "Inbox",
	NEED_ACTION: "Needs action",
	REQUEST_SENT: "Requests sent",
	FEEDBACK_SENT: "Feedback sent",
	DRAFT: "Draft",
	ARCHIVED: "Archived",
} as const;

interface Option extends SelectorOption {
	readonly value: FeedbackCategory;
}

const FeedbackOptions: readonly Option[] = [
	{ value: FEEDBACK_CATEGORY_VALUES.INBOX, label: FEEDBACK_CATEGORY_TEXT_MAP.INBOX },
	{
		value: FEEDBACK_CATEGORY_VALUES.NEED_ACTION,
		label: FEEDBACK_CATEGORY_TEXT_MAP.NEED_ACTION,
		"data-test-id": "feedback-category-needs-action",
	},
	{ value: FEEDBACK_CATEGORY_VALUES.REQUEST_SENT, label: FEEDBACK_CATEGORY_TEXT_MAP.REQUEST_SENT },
	{ value: FEEDBACK_CATEGORY_VALUES.FEEDBACK_SENT, label: FEEDBACK_CATEGORY_TEXT_MAP.FEEDBACK_SENT },
	{ value: FEEDBACK_CATEGORY_VALUES.DRAFT, label: FEEDBACK_CATEGORY_TEXT_MAP.DRAFT },
	{ value: FEEDBACK_CATEGORY_VALUES.ARCHIVED, label: FEEDBACK_CATEGORY_TEXT_MAP.ARCHIVED },
];

interface Props {
	value: FeedbackCategory;
	setView: (v: FeedbackCategory) => void;
	className?: string;
}

export default function Dropdown({ value, setView }: Props) {
	const onChangeValue = React.useCallback(
		(feedbackCategory: SingleValue<Option>) => {
			if (feedbackCategory) setView(feedbackCategory.value);
		},
		[setView],
	);

	return (
		<Selector
			condensed
			data-test-id="feedback-category-list"
			value={FeedbackOptions.find(o => o.value === value)}
			options={FeedbackOptions}
			onChange={onChangeValue}
		/>
	);
}
